import React, { useCallback, useEffect, useState } from 'react';
import { courseSupportingDataService } from '../../../../services/courseSupportingDataService';

export function SummaryEditor({ assetId }: { assetId: string }) {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setSavedMessage(null);
      try {
        const data = await courseSupportingDataService.getSummary(assetId);
        if (cancelled) return;
        setContent(data.content);
        setOriginalContent(data.content);
        setEtag(data.etag);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assetId]);

  const isDirty = content !== originalContent;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const result = await courseSupportingDataService.updateSummary(assetId, content, etag);
      setEtag(result.etag);
      setOriginalContent(content);
      setSavedMessage('Saved successfully');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [content, etag, assetId]);

  const handleDiscard = () => {
    setContent(originalContent);
    setError(null);
    setSavedMessage(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <textarea
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        rows={20}
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Summary'}
        </button>
        <button
          onClick={handleDiscard}
          disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Discard
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>
      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>The summary is used by directional stages (e.g. syllabus generation) that need awareness of this document without the full detail. The full document is used by drafting stages that produce learner-facing prose.</span>
      </div>
    </div>
  );
}
